import { formatUnits, toBigInt } from "ethers";
import { ABIS, ERC20, lockerAbi, QUOTER_ABI } from "../../Contracts/V3/abis";
import { ADDRS } from "../../Contracts/V3/addresses";
import { publicClient, walletClient } from "../../Contracts/V3/client";
import {
  encodeSqrtPriceX96,
  spacingForFee,
  nearestUsableTick,
} from "../../Utils/tickMath";
import { encodeFunctionData } from "viem";
import { toast } from "react-toastify";

// price = token1 per token0 (after address sorting!)
function tickFromPrice(price, dec0, dec1) {
  if (!(price > 0)) throw new Error("price must be > 0");
  const priceAdj = price * Math.pow(10, dec0 - dec1); // adjust for decimals
  return Math.floor(Math.log(priceAdj) / Math.log(1.0001));
}

function nearestUsableDown(tick, spacing) {
  return Math.floor(tick / spacing) * spacing;
}
function nearestUsableUp(tick, spacing) {
  return Math.ceil(tick / spacing) * spacing;
}

export function ticksFromMinMaxPrice({ minPrice, maxPrice, fee, dec0, dec1 }) {
  // if (!(minPrice > 0) || !(maxPrice > 0) || !(maxPrice > minPrice)) {
  //   throw new Error("Invalid price range");
  // }
  const spacing = spacingForFee(fee);

  const rawLower = tickFromPrice(minPrice, dec0, dec1);
  const rawUpper = tickFromPrice(maxPrice, dec0, dec1);

  let tickLower = nearestUsableDown(rawLower, spacing);
  let tickUpper = nearestUsableUp(rawUpper, spacing);

  tickLower = Math.max(tickLower, Math.ceil(MIN_TICK / spacing) * spacing);
  tickUpper = Math.min(tickUpper, Math.floor(MAX_TICK / spacing) * spacing);
  if (tickLower >= tickUpper) tickUpper = tickLower + spacing;
  return { tickLower, tickUpper };
}

// -------------------- helpers / constants --------------------
async function getAccount() {
  if (!walletClient) throw new Error("No wallet client (connect your wallet).");
  const [acc] = await walletClient.requestAddresses();
  if (!acc) throw new Error("No account found. Connect your wallet first.");
  return acc;
}

// helper: works for viem status variants ("success"/"reverted" OR 1/0)
function isTxSuccess(receipt) {
  return (
    receipt &&
    (receipt.status === "success" ||
      receipt.status === 1 ||
      receipt.status === 1n)
  );
}

async function wethwBalance(account) {
  return publicClient.readContract({
    address: ADDRS.WETHW,
    abi: ERC20,
    functionName: "balanceOf",
    args: [account],
  });
}

export async function unwrapWETHW(amountWei) {
  if (typeof amountWei !== "bigint" || amountWei <= 0n) return;

  const account = await getAccount();
  const hash = await walletClient.writeContract({
    account,
    address: ADDRS.WETHW,
    abi: WETH9_ABI,
    functionName: "withdraw",
    args: [amountWei],
  });
  const receipt = await publicClient.waitForTransactionReceipt({ hash });
  if (!isTxSuccess(receipt))
    throw new Error(`Unwrap reverted: ${receipt.transactionHash}`);
  return receipt;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function decimals(token) {
  return publicClient.readContract({
    address: token,
    abi: ERC20,
    functionName: "decimals",
  });
}

const ZERO = "0x0000000000000000000000000000000000000000";
const isZero = (a) => !a || a.toLowerCase() === ZERO.toLowerCase();

const WETH9_ABI = [
  {
    type: "function",
    name: "deposit",
    stateMutability: "payable",
    inputs: [],
    outputs: [],
  },
  {
    type: "function",
    name: "withdraw",
    stateMutability: "nonpayable",
    inputs: [{ name: "wad", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "a", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
];

const WETHW = (ADDRS?.WETHW || "").toLowerCase();
const isWethw = (addr) => addr && addr.toLowerCase() === WETHW;

// pick a stable quoter address (accept several possible keys)
const QUOTER_ADDR =
  ADDRS.QUOTER ??
  ADDRS.Quoter ??
  ADDRS.quoter ??
  ADDRS.QUOTER_V2 ??
  ADDRS.QuoterV2 ??
  ADDRS.quoterV2;

if (!QUOTER_ADDR) throw new Error("Quoter address not set in ADDRS");

// lexicographic sort helper for factory.getPool lookups
export function sortLex(a, b) {
  return a?.toLowerCase() < b?.toLowerCase() ? [a, b] : [b, a];
}

// -------------------- allowances --------------------
export async function allowance(owner, token, spender) {
  return publicClient.readContract({
    address: token,
    abi: ERC20,
    functionName: "allowance",
    args: [owner, spender],
  });
}

export async function approve(spender, token, amount) {
  const hash = await walletClient.writeContract({
    account: await getAccount(),
    address: token,
    abi: ERC20,
    functionName: "approve",
    args: [spender, amount],
  });
  return publicClient.waitForTransactionReceipt({ hash });
}

// Router-focused wrappers expected by your UI
export async function getAllowance(owner, token) {
  return allowance(owner, token, ADDRS.SWAP_ROUTER);
}
export async function approveMax(token, amount) {
  return approve(ADDRS.SWAP_ROUTER, token, amount);
}

// -------------------- factory / pool --------------------
export async function getPoolAddress(tokenA, tokenB, fee) {
  const [token0, token1] = sortLex(tokenA, tokenB);
  return publicClient.readContract({
    address: ADDRS.V3_FACTORY,
    abi: ABIS.factory,
    functionName: "getPool",
    args: [token0, token1, fee],
  });
}

export async function getPoolStatus(tokenA, tokenB, fee) {
  const pool = await getPoolAddress(tokenA, tokenB, fee);
  if (!pool || isZero(pool))
    return { exists: false, initialized: false, pool: null, tick: null };
  const [sqrtPriceX96, tick] = await publicClient.readContract({
    address: pool,
    abi: ABIS.pool,
    functionName: "slot0",
  });
  const initialized = sqrtPriceX96 !== 0n;
  return {
    exists: true,
    initialized,
    pool,
    tick: initialized ? Number(tick) : null,
  };
}

// -------------------- create + initialize pool --------------------
// export async function createAndInitPool({ token0, token1, fee, initPrice }) {
//   if (token0.toLowerCase() >= token1.toLowerCase()) {
//     throw new Error("TOKEN0 must be lexicographically smaller than TOKEN1");
//   }
//   const [dec0, dec1] = await Promise.all([decimals(token0), decimals(token1)]);
//   const sqrt = encodeSqrtPriceX96(
//     Number(initPrice),
//     Number(dec0),
//     Number(dec1)
//   );

//   const account = await getAccount();
//   const sim = await publicClient.simulateContract({
//     account,
//     address: ADDRS.POSITION_MANAGER,
//     abi: ABIS.npm,
//     functionName: "createAndInitializePoolIfNecessary",
//     args: [token0, token1, fee, sqrt],
//   });

//   const txHash = await walletClient.writeContract(sim.request);
//   const receipt = await publicClient.waitForTransactionReceipt({
//     hash: txHash,
//   });

//   const pool = await getPoolAddress(token0, token1, fee);
//   return { pool, tx: txHash, receipt };
// }
export async function createAndInitPool({ token0, token1, fee, initPrice }) {
  const [t0, t1] = sortLex(token0, token1);
  const orderChanged = t0.toLowerCase() !== token0.toLowerCase();

  const adjustedPrice = orderChanged
    ? 1 / Number(initPrice)
    : Number(initPrice);

  // Fetch token decimals
  const [dec0, dec1] = await Promise.all([decimals(t0), decimals(t1)]);

  // Encode sqrtPriceX96 from the adjusted price
  const sqrt = encodeSqrtPriceX96(adjustedPrice, Number(dec0), Number(dec1));

  // Prepare transaction
  const account = await getAccount();
  const sim = await publicClient.simulateContract({
    account,
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "createAndInitializePoolIfNecessary",
    args: [t0, t1, fee, sqrt],
  });

  // Send transaction
  const txHash = await walletClient.writeContract(sim.request);

  // Wait for receipt
  const receipt = await publicClient.waitForTransactionReceipt({
    hash: txHash,
  });

  // Get pool address
  const pool = await getPoolAddress(t0, t1, fee);
  return { pool, tx: txHash, receipt };
}

// -------------------- wrapping helpers --------------------
export async function wrapWETHW(amountWei) {
  if (!WETHW) throw new Error("ADDRS.WETHW is not set; cannot wrap.");
  const account = await getAccount();
  const balNative = await publicClient.getBalance({ address: account });
  if (balNative < amountWei)
    throw new Error(
      `Not enough native ETHW to wrap: need ${amountWei} have ${balNative}`
    );

  const sim = await publicClient.simulateContract({
    account,
    address: ADDRS.WETHW,
    abi: WETH9_ABI,
    functionName: "deposit",
    args: [],
    value: amountWei,
  });
  const hash = await walletClient.writeContract(sim.request);
  return publicClient.waitForTransactionReceipt({ hash });
}

async function ensureWethAmount(token, desired, label, debugLabel) {
  if (!WETHW) return;
  if (!isWethw(token)) return;

  const addr = await getAccount();
  const cur = await publicClient
    .readContract({
      address: ADDRS.WETHW,
      abi: WETH9_ABI,
      functionName: "balanceOf",
      args: [addr],
    })
    .catch(() => 0n);

  if (cur >= desired) return;
  const missing = desired - cur;
  await wrapWETHW(missing);
  return missing;
}

// -------------------- positions --------------------
export async function getPosition(tokenId) {
  const r = await publicClient.readContract({
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "positions",
    args: [toBigInt(tokenId)],
  });
  return {
    nonce: r[0],
    operator: r[1],
    token0: r[2],
    token1: r[3],
    fee: Number(r[4]),
    tickLower: Number(r[5]),
    tickUpper: Number(r[6]),
    liquidity: r[7],
    feeGrowth0Last: r[8],
    feeGrowth1Last: r[9],
    tokensOwed0: r[10],
    tokensOwed1: r[11],
  };
}

// -------------------- positions --------------------
const Q96 = 1n << 96n;
const MAX_TICK = 887272;
const MIN_TICK = -MAX_TICK;

// ---------- math helpers ----------
function mulDivRoundingDown(a, b, d) {
  return (a * b) / d;
}
function mulDivRoundingUp(a, b, d) {
  const prod = a * b;
  let res = prod / d;
  if (prod % d !== 0n) res += 1n;
  return res;
}

// Liquidity <-> amounts (Uniswap v3 exact formulas)
function getLiquidityForAmount0_Rdown(sa, sb, amount0) {
  if (sa > sb) [sa, sb] = [sb, sa];
  const inter = mulDivRoundingDown(sa, sb, Q96); // (sa*sb)/Q96
  return mulDivRoundingDown(amount0, inter, sb - sa); // floor(amount0*inter/(sb-sa))
}
function getLiquidityForAmount1_Rdown(sa, sb, amount1) {
  if (sa > sb) [sa, sb] = [sb, sa];
  return mulDivRoundingDown(amount1, Q96, sb - sa); // floor(amount1*Q96/(sb-sa))
}

function getAmount0ForLiquidity_Rup(sa, sb, L) {
  if (sa > sb) [sa, sb] = [sb, sa];
  // ceil(L * (sb - sa) * Q96 / (sa*sb))
  const num = L * (sb - sa) * Q96;
  const den = sa * sb;
  return mulDivRoundingUp(num, 1n, den);
}
function getAmount1ForLiquidity_Rup(sa, sb, L) {
  if (sa > sb) [sa, sb] = [sb, sa];
  // ceil(L * (sb - sa) / Q96)
  const num = L * (sb - sa);
  return mulDivRoundingUp(num, 1n, Q96);
}

function getSqrtRatioAtTick(tick) {
  if (tick < MIN_TICK || tick > MAX_TICK) throw new Error("tick out of bounds");
  let absTick = tick < 0 ? -tick : tick;

  let ratio =
    (absTick & 0x1) !== 0
      ? 0xfffcb933bd6fad37aa2d162d1a594001n
      : 0x100000000000000000000000000000000n;
  if ((absTick & 0x2) !== 0)
    ratio = (ratio * 0xfff97272373d413259a46990580e213an) >> 128n;
  if ((absTick & 0x4) !== 0)
    ratio = (ratio * 0xfff2e50f5f656932ef12357cf3c7fdccn) >> 128n;
  if ((absTick & 0x8) !== 0)
    ratio = (ratio * 0xffe5caca7e10e4e61c3624eaa0941cd0n) >> 128n;
  if ((absTick & 0x10) !== 0)
    ratio = (ratio * 0xffcb9843d60f6159c9db58835c926644n) >> 128n;
  if ((absTick & 0x20) !== 0)
    ratio = (ratio * 0xff973b41fa98c081472e6896dfb254c0n) >> 128n;
  if ((absTick & 0x40) !== 0)
    ratio = (ratio * 0xff2ea16466c96a3843ec78b326b52861n) >> 128n;
  if ((absTick & 0x80) !== 0)
    ratio = (ratio * 0xfe5dee046a99a2a811c461f1969c3053n) >> 128n;
  if ((absTick & 0x100) !== 0)
    ratio = (ratio * 0xfcbe86c7900a88aedcffc83b479aa3a4n) >> 128n;
  if ((absTick & 0x200) !== 0)
    ratio = (ratio * 0xf987a7253ac413176f2b074cf7815e54n) >> 128n;
  if ((absTick & 0x400) !== 0)
    ratio = (ratio * 0xf3392b0822b70005940c7a398e4b70f3n) >> 128n;
  if ((absTick & 0x800) !== 0)
    ratio = (ratio * 0xe7159475a2c29b7443b29c7fa6e889d9n) >> 128n;
  if ((absTick & 0x1000) !== 0)
    ratio = (ratio * 0xd097f3bdfd2022b8845ad8f792aa5825n) >> 128n;
  if ((absTick & 0x2000) !== 0)
    ratio = (ratio * 0xa9f746462d870fdf8a65dc1f90e061e5n) >> 128n;
  if ((absTick & 0x4000) !== 0)
    ratio = (ratio * 0x70d869a156d2a1b890bb3df62baf32f7n) >> 128n;
  if ((absTick & 0x8000) !== 0)
    ratio = (ratio * 0x31be135f97d08fd981231505542fcfa6n) >> 128n;
  if ((absTick & 0x10000) !== 0)
    ratio = (ratio * 0x9aa508b5b7a84e1c677de54f3e99bc9n) >> 128n;
  if ((absTick & 0x20000) !== 0)
    ratio = (ratio * 0x5d6af8dedb81196699c329225ee604n) >> 128n;
  if ((absTick & 0x40000) !== 0)
    ratio = (ratio * 0x2216e584f5fa1ea926041bedfe98n) >> 128n;
  if ((absTick & 0x80000) !== 0)
    ratio = (ratio * 0x48a170391f7dc42444e8fa2n) >> 128n;

  if (tick > 0) {
    // Inverse with rounding up
    const max = (1n << 256n) - 1n;
    ratio = max / ratio + (max % ratio === 0n ? 0n : 1n);
  }
  // Q128.128 -> Q64.96 (>> 32) with rounding up
  const q32 = 1n << 32n;
  const sqrtPriceX96 = (ratio >> 32n) + (ratio % q32 === 0n ? 0n : 1n);
  return sqrtPriceX96;
}

// -------------------- quote for add liquidity ------------
export async function quoteMintAmountsGivenOneSide({
  token0,
  token1,
  fee,
  tickLower,
  tickUpper,
  amount0Desired = 0n,
  amount1Desired = 0n,
}) {
  // if (token0.toLowerCase() >= token1.toLowerCase()) {
  //   throw new Error("TOKEN0 must be < TOKEN1");
  // }
  if (
    (amount0Desired > 0n && amount1Desired > 0n) ||
    (amount0Desired === 0n && amount1Desired === 0n)
  ) {
    throw new Error("Provide exactly one of amount0Desired or amount1Desired.");
  }
  if (tickLower >= tickUpper) throw new Error("tickLower must be < tickUpper");

  // Read pool price
  const pool = await publicClient.readContract({
    address: await (async () => {
      // you likely already have getPoolAddress; if not, pass pool address directly
      // @ts-ignore
      return await getPoolAddress(token0, token1, fee);
    })(),
    abi: ABIS.pool,
    functionName: "slot0",
  });
  const sqrtP = toBigInt(Array.isArray(pool) ? pool[0] : pool.sqrtPriceX96);
  const currTick = Number(Array.isArray(pool) ? pool[1] : pool.tick);

  if (!sqrtP || sqrtP === 0n)
    throw new Error("Pool exists but is not initialized.");

  const sa = getSqrtRatioAtTick(tickLower);
  const sb = getSqrtRatioAtTick(tickUpper);
  const sp = sqrtP;

  // Determine region
  const region =
    currTick < tickLower ? "below" : currTick > tickUpper ? "above" : "inside";

  let L = 0n;
  let amount0Used = 0n;
  let amount1Used = 0n;

  if (amount0Desired > 0n) {
    if (region === "above") {
      throw new Error(
        "Current price is ABOVE your range; token0 won't be used. Provide token1 or widen the range."
      );
    }
    // Liquidity from amount0 (round down)
    L =
      region === "below"
        ? getLiquidityForAmount0_Rdown(sa, sb, amount0Desired)
        : getLiquidityForAmount0_Rdown(sp, sb, amount0Desired);

    if (L === 0n)
      return { amount0Desired, amount1Desired: 0n, liquidity: 0n, region };

    // Counterpart amount1 (round up). Also compute how much of token0 is actually used.
    amount1Used =
      region === "below" ? 0n : getAmount1ForLiquidity_Rup(sa, sp, L);
    amount0Used =
      region === "below"
        ? getAmount0ForLiquidity_Rup(sa, sb, L)
        : getAmount0ForLiquidity_Rup(sp, sb, L);

    // We keep user's amount0Desired as-is; they should approve at least that.
    return {
      amount0Desired,
      amount1Desired: amount1Used,
      liquidity: L,
      region,
      used0: amount0Used,
      used1: amount1Used,
    };
  } else {
    // amount1Desired > 0n
    if (region === "below") {
      throw new Error(
        "Current price is BELOW your range; token1 won't be used. Provide token0 or widen the range."
      );
    }
    // Liquidity from amount1 (round down)
    L =
      region === "above"
        ? getLiquidityForAmount1_Rdown(sa, sb, amount1Desired)
        : getLiquidityForAmount1_Rdown(sa, sp, amount1Desired);

    if (L === 0n)
      return { amount0Desired: 0n, amount1Desired, liquidity: 0n, region };

    // Counterpart amount0 (round up). Also compute how much of token1 is actually used.
    amount0Used =
      region === "above" ? 0n : getAmount0ForLiquidity_Rup(sp, sb, L);
    amount1Used =
      region === "above"
        ? getAmount1ForLiquidity_Rup(sa, sb, L)
        : getAmount1ForLiquidity_Rup(sa, sp, L);

    return {
      amount0Desired: amount0Used,
      amount1Desired,
      liquidity: L,
      region,
      used0: amount0Used,
      used1: amount1Used,
    };
  }
}

// -------------------- mint liquidity (auto-wrap to match user input) --------------------
// export async function mintLiquidity({
//   token0,
//   token1,
//   fee,
//   amount0Desired,
//   amount1Desired,
//   bandPct = 0.1,
//   debugLabel = "v3.mint",
// }) {
//   if (token0.toLowerCase() >= token1.toLowerCase())
//     throw new Error("TOKEN0 must be < TOKEN1");
//   const addr = await getAccount();

//   const pool = await getPoolAddress(token0, token1, fee);
//   if (!pool || isZero(pool)) {
//     const pct = (fee / 10000).toFixed(2);
//     throw new Error(`Pool not found for fee ${pct}%. Create/initialize first.`);
//   }

//   const slot0 = await publicClient.readContract({
//     address: pool,
//     abi: ABIS.pool,
//     functionName: "slot0",
//   });
//   const sqrtPriceX96 = slot0[0];
//   const currentTick = Number(slot0[1]);
//   if (!sqrtPriceX96 || sqrtPriceX96 === 0n || !Number.isFinite(currentTick)) {
//     throw new Error(
//       "Pool exists but is not initialized (sqrtPriceX96=0). Initialize first."
//     );
//   }

//   const spacing = spacingForFee(fee);
//   const MIN_TICK = -887272;
//   const MAX_TICK = 887272;
//   const MIN_USABLE = Math.ceil(MIN_TICK / spacing) * spacing;
//   const MAX_USABLE = Math.floor(MAX_TICK / spacing) * spacing;

//   const rawDelta = Math.floor(Math.log(1 + bandPct) / Math.log(1.0001));
//   const delta = Math.max(spacing, Math.floor(rawDelta / spacing) * spacing);

//   let tickLower = nearestUsableTick(currentTick - delta, spacing);
//   let tickUpper = nearestUsableTick(currentTick + delta, spacing);
//   if (tickLower < MIN_USABLE) tickLower = MIN_USABLE;
//   if (tickUpper > MAX_USABLE) tickUpper = MAX_USABLE;
//   if (tickLower >= tickUpper) tickUpper = tickLower + spacing;

//   const addrBalAllow = async (tkn) => {
//     const [b, a] = await Promise.all([
//       publicClient
//         .readContract({
//           address: tkn,
//           abi: ERC20,
//           functionName: "balanceOf",
//           args: [addr],
//         })
//         .catch(() => 0n),
//       allowance(addr, tkn, ADDRS.POSITION_MANAGER).catch(() => 0n),
//     ]);
//     return [b, a];
//   };
//   const [[bal0, allow0], [bal1, allow1]] = await Promise.all([
//     addrBalAllow(token0),
//     addrBalAllow(token1),
//   ]);

//   console.groupCollapsed(`[${debugLabel}] preflight`);
//   console.table([
//     { k: "amount0Desired", v: amount0Desired.toString() },
//     { k: "amount1Desired", v: amount1Desired.toString() },
//     { k: "bal0", v: bal0.toString() },
//     { k: "bal1", v: bal1.toString() },
//     { k: "allow0→NPM", v: allow0.toString() },
//     { k: "allow1→NPM", v: allow1.toString() },
//     { k: "WETHW", v: ADDRS?.WETHW || "(unset)" },
//   ]);
//   console.groupEnd();

//   await ensureWethAmount(token0, amount0Desired, "token0", debugLabel);
//   await ensureWethAmount(token1, amount1Desired, "token1", debugLabel);

//   const [allow0Now, allow1Now] = await Promise.all([
//     allowance(addr, token0, ADDRS.POSITION_MANAGER).catch(() => 0n),
//     allowance(addr, token1, ADDRS.POSITION_MANAGER).catch(() => 0n),
//   ]);
//   if (amount0Desired > 0n && allow0Now < amount0Desired)
//     await approve(ADDRS.POSITION_MANAGER, token0, amount0Desired);
//   if (amount1Desired > 0n && allow1Now < amount1Desired)
//     await approve(ADDRS.POSITION_MANAGER, token1, amount1Desired);

//   const deadline = toBigInt(Math.floor(Date.now() / 1000) + 600);

//   const sim = await publicClient.simulateContract({
//     account: addr,
//     address: ADDRS.POSITION_MANAGER,
//     abi: ABIS.npm,
//     functionName: "mint",
//     args: [
//       {
//         token0,
//         token1,
//         fee,
//         tickLower,
//         tickUpper,
//         amount0Desired,
//         amount1Desired,
//         amount0Min: 0n,
//         amount1Min: 0n,
//         recipient: addr,
//         deadline,
//       },
//     ],
//   });

//   const txHash = await walletClient.writeContract(sim.request);
//   const receipt = await publicClient.waitForTransactionReceipt({
//     hash: txHash,
//   });

//   let tokenIdSim, liqSim, used0Sim, used1Sim;
//   if (Array.isArray(sim.result))
//     [tokenIdSim, liqSim, used0Sim, used1Sim] = sim.result;
//   else
//     ({
//       tokenId: tokenIdSim,
//       liquidity: liqSim,
//       amount0: used0Sim,
//       amount1: used1Sim,
//     } = sim.result);

//   const pos = await publicClient.readContract({
//     address: ADDRS.POSITION_MANAGER,
//     abi: ABIS.npm,
//     functionName: "positions",
//     args: [toBigInt(tokenIdSim)],
//   });

//   return {
//     txHash,
//     receipt,
//     tokenId: tokenIdSim,
//     liquidity: liqSim,
//     amount0Used: used0Sim,
//     amount1Used: used1Sim,
//     tickLower,
//     tickUpper,
//     position: {
//       nonce: pos[0],
//       operator: pos[1],
//       token0: pos[2],
//       token1: pos[3],
//       fee: Number(pos[4]),
//       tickLower: Number(pos[5]),
//       tickUpper: Number(pos[6]),
//       liquidity: pos[7],
//       feeGrowth0: pos[8],
//       feeGrowth1: pos[9],
//       owed0: pos[10],
//       owed1: pos[11],
//     },
//   };
// }

export async function mintLiquidity({
  token0,
  token1,
  fee,
  amount0Desired,
  amount1Desired,
  rangeMode = "full",
  minPrice,
  maxPrice,
  // bandPct = 0.1,
  debugLabel = "v3.mint",
  translate,
}) {
  const [t0, t1] = sortLex(token0, token1);

  token0 = t0;
  token1 = t1;
  const addr = await getAccount();
  let wrapped0 = 0n;
  let wrapped1 = 0n;

  try {
    const pool = await getPoolAddress(token0, token1, fee);
    if (!pool || isZero(pool)) {
      const pct = (fee / 10000).toFixed(2);
      throw new Error(
        `Pool not found for fee ${pct}%. Create/initialize first.`
      );
    }

    const slot0 = await publicClient.readContract({
      address: pool,
      abi: ABIS.pool,
      functionName: "slot0",
    });
    const sqrtPriceX96 = Array.isArray(slot0) ? slot0[0] : slot0.sqrtPriceX96;

    const currentTick = Number(Array.isArray(slot0) ? slot0[1] : slot0.tick);

    if (!sqrtPriceX96 || sqrtPriceX96 === 0n || !Number.isFinite(currentTick)) {
      throw new Error(
        "Pool exists but is not initialized (sqrtPriceX96=0). Initialize first."
      );
    }

    const tickToPrice = (tick) => Math.pow(1.0001, tick);

    const spacing = spacingForFee(fee);
    const MIN_TICK = -887272;
    const MAX_TICK = 887272;
    const MIN_USABLE = Math.ceil(MIN_TICK / spacing) * spacing;
    const MAX_USABLE = Math.floor(MAX_TICK / spacing) * spacing;

    const fullMinTickPrice = tickToPrice(MIN_TICK);
    const fullMaxTickPrice = tickToPrice(MAX_TICK);

    let tickLower, tickUpper;

    if (rangeMode === "full") {
      tickLower = MIN_USABLE;
      tickUpper = MAX_USABLE;
    } else {
      if (!minPrice || !maxPrice)
        throw new Error(
          "Custom range selected but minPrice/maxPrice not provided."
        );

      const priceToTick = (price) =>
        Math.floor(Math.log(price) / Math.log(1.0001));

      tickLower = nearestUsableTick(priceToTick(minPrice), spacing);
      tickUpper = nearestUsableTick(priceToTick(maxPrice), spacing);

      if (tickLower < MIN_USABLE) tickLower = MIN_USABLE;
      if (tickUpper > MAX_USABLE) tickUpper = MAX_USABLE;
      if (tickLower >= tickUpper) tickUpper = tickLower + spacing;
    }

    const addrBalAllow = async (tkn) => {
      const [b, a] = await Promise.all([
        publicClient
          .readContract({
            address: tkn,
            abi: ERC20,
            functionName: "balanceOf",
            args: [addr],
          })
          .catch(() => 0n),
        allowance(addr, tkn, ADDRS.POSITION_MANAGER).catch(() => 0n),
      ]);
      return [b, a];
    };

    const [[bal0, allow0], [bal1, allow1]] = await Promise.all([
      addrBalAllow(token0),
      addrBalAllow(token1),
    ]);

    // --- Wrap missing WETHW (and remember how much we wrapped) ---
    wrapped0 = await ensureWethAmount(
      token0,
      amount0Desired,
      "token0",
      debugLabel
    );
    wrapped1 = await ensureWethAmount(
      token1,
      amount1Desired,
      "token1",
      debugLabel
    );

    const [allow0Now, allow1Now] = await Promise.all([
      allowance(addr, token0, ADDRS.POSITION_MANAGER).catch(() => 0n),
      allowance(addr, token1, ADDRS.POSITION_MANAGER).catch(() => 0n),
    ]);

    // if (amount0Desired > 0n && allow0Now < amount0Desired)
    //   await approve(ADDRS.POSITION_MANAGER, token0, amount0Desired);

    // if (amount1Desired > 0n && allow1Now < amount1Desired)
    //   await approve(ADDRS.POSITION_MANAGER, token1, amount1Desired);
    if (amount0Desired > 0n && allow0Now < amount0Desired) {
      await approve(ADDRS.POSITION_MANAGER, token0, amount0Desired);
    }
    if (amount1Desired > 0n && allow1Now < amount1Desired) {
      await approve(ADDRS.POSITION_MANAGER, token1, amount1Desired);
    }

    const deadline = toBigInt(Math.floor(Date.now() / 1000) + 600);

    const sim = await publicClient.simulateContract({
      account: addr,
      address: ADDRS.POSITION_MANAGER,
      abi: ABIS.npm,
      functionName: "mint",
      args: [
        {
          token0,
          token1,
          fee,
          tickLower,
          tickUpper,
          amount0Desired,
          amount1Desired,
          amount0Min: 0n,
          amount1Min: 0n,
          recipient: addr,
          deadline,
        },
      ],
    });
    const txHash = await walletClient.writeContract(sim.request);
    const receipt = await publicClient.waitForTransactionReceipt({
      hash: txHash,
    });

    // if (receipt.status !== "success") {
    //   const err = new Error(`V3 mint reverted: ${txHash}`);
    //   err.txHash = txHash;
    //   err.receipt = receipt;
    //   throw err;
    // }

    let tokenIdSim, liqSim, used0Sim, used1Sim;
    if (Array.isArray(sim.result))
      [tokenIdSim, liqSim, used0Sim, used1Sim] = sim.result;
    else
      ({
        tokenId: tokenIdSim,
        liquidity: liqSim,
        amount0: used0Sim,
        amount1: used1Sim,
      } = sim.result);

    const pos = await publicClient.readContract({
      address: ADDRS.POSITION_MANAGER,
      abi: ABIS.npm,
      functionName: "positions",
      args: [toBigInt(tokenIdSim)],
    });

    // async function readErc20(publicClient, tokenAddress, fn, args = []) {
    //   return publicClient.readContract({
    //     address: tokenAddress,
    //     abi: ERC20,
    //     functionName: fn,
    //     args,
    //   });
    // }

    // const tokenReserve = await readErc20(
    //   publicClient,
    //   token1Address,
    //   "balanceOf",
    //   [pool]
    // );
    // const ethwReserve = await readErc20(publicClient, ADDRS.WETHW, "balanceOf", [
    //   pool,
    // ]);
    // const totalSupply = await readErc20(
    //   publicClient,
    //   token1Address,
    //   "totalSupply"
    // );
    // const tokenBalance = await readErc20(
    //   publicClient,
    //   token1Address,
    //   "balanceOf",
    //   [addr]
    // );

    return {
      amount0Desired,
      amount1Desired,
      txHash,
      receipt,
      tokenId: tokenIdSim,
      liquidity: liqSim,
      amount0Used: used0Sim,
      amount1Used: used1Sim,
      tickLower,
      tickUpper,
      sqrtPriceX96,
      currentTick,
      fullMinTickPrice,
      fullMaxTickPrice,
      spacing,
      pool,
      position: {
        nonce: pos[0],
        operator: pos[1],
        token0: pos[2],
        token1: pos[3],
        fee: Number(pos[4]),
        tickLower: Number(pos[5]),
        tickUpper: Number(pos[6]),
        liquidity: pos[7],
        feeGrowth0: pos[8],
        feeGrowth1: pos[9],
        owed0: pos[10],
        owed1: pos[11],
      },
    };
  } catch (err) {
    // --- Unwrap only what THIS mint call wrapped ---
    // const delta = wrapped0 + wrapped1;
    console.log("Error", err);
    toast.error(err.shortMessage);
    const delta = (wrapped0 ?? 0n) + (wrapped1 ?? 0n);
    if (delta > 0n) {
      //       toast.info(
      // "Transaction reverted. Initiating unwrap transaction to return ETHW to your wallet "     );
      toast.info(translate("createLiq.unWrappedMsg"));

      await sleep(1700);
      try {
        await unwrapWETHW(delta);
        // toast.success("Unwrapped back to ETHW.");
        toast.success(translate("createLiq.unWrappedSuccessMsg"));
      } catch {
        toast.error(translate("createLiq.unWrappedErrorMsg"));
      }
    }
    // throw err;
  }
}

// -------------------- quotes --------------------
// export async function quoteExactIn(arg) {
//   let tokenIn, tokenOut, fee, amountIn;
//   if (typeof arg === "bigint") {
//     tokenIn = ADDRS.TOKEN0;
//     tokenOut = ADDRS.TOKEN1;
//     fee = ADDRS.FEE;
//     amountIn = arg;
//   } else {
//     ({ tokenIn, tokenOut, feeTierBps: fee, amountIn } = arg);
//   }

//   if (amountIn === 0n) return 0n;
//   if (typeof fee !== "number" || fee <= 0)
//     throw new Error("Fee must be uint24 (e.g., 3000).");

//   const st = await getPoolStatus(tokenIn, tokenOut, fee);
//   if (!st.exists)
//     throw new Error(`Pool not found for fee ${(fee / 10000).toFixed(2)}%`);
//   if (!st.initialized)
//     throw new Error(
//       `Pool exists at ${(fee / 10000).toFixed(2)}% but is not initialized`
//     );

//   // IMPORTANT: QuoterV2 tuple order = tokenIn, tokenOut, amountIn, fee, sqrtPriceLimitX96
//   const r = await publicClient.readContract({
//     address: QUOTER_ADDR,
//     abi: QUOTER_ABI,
//     functionName: "quoteExactInputSingle",
//     args: [[tokenIn, tokenOut, amountIn, fee, 0n]],
//   });
//   const out = Array.isArray(r) ? r[0] : r.amountOut ?? r;
//   return out;
// }

// NEW: single-call exact-output quote (v1/v2)
// export async function quoteExactOut({ tokenIn, tokenOut, fee, amountOut }) {
//   if (amountOut === 0n) return 0n;
//   const st = await getPoolStatus(tokenIn, tokenOut, fee);
//   if (!st.exists)
//     throw new Error(`Pool not found for fee ${(fee / 10000).toFixed(2)}%`);
//   if (!st.initialized)
//     throw new Error(
//       `Pool exists at ${(fee / 10000).toFixed(2)}% but is not initialized`
//     );

//   try {
//     // Quoter v1
//     const ain = await publicClient.readContract({
//       address: QUOTER_ADDR,
//       abi: QUOTER_ABI,
//       functionName: "quoteExactOutputSingle",
//       args: [tokenIn, tokenOut, fee, amountOut, 0n],
//     });
//     return ain;
//   } catch {
//     // Quoter v2
//     const r = await publicClient.readContract({
//       address: QUOTER_ADDR,
//       abi: QUOTER_ABI,
//       functionName: "quoteExactOutputSingle",
//       args: [{ tokenIn, tokenOut, fee, amountOut, sqrtPriceLimitX96: 0n }],
//     });
//     return Array.isArray(r) ? r[0] : r.amountIn ?? r;
//   }
// }

// EXACT IN: user types amountIn of tokenIn -> get amountOut of tokenOut
export async function quoteExactIn({ tokenIn, tokenOut, fee, amountIn }) {
  const [amountOut, sqrtAfter, ticks, gas] = await publicClient.readContract({
    address: QUOTER_ADDR,
    abi: QUOTER_ABI,
    functionName: "quoteExactInputSingle",
    args: [
      {
        tokenIn,
        tokenOut,
        fee,
        amountIn,
        sqrtPriceLimitX96: 0n,
      },
    ],
  });

  return { amountOut, sqrtAfter, ticks, gas };
}

// EXACT OUT: user types desired amountOut of tokenOut -> get required amountIn of tokenIn
export async function quoteExactOut({ tokenIn, tokenOut, fee, amountOut }) {
  const [amountIn, sqrtAfter, ticks, gas] = await publicClient.readContract({
    address: QUOTER_ADDR,
    abi: QUOTER_ABI,
    functionName: "quoteExactOutputSingle",
    args: [
      {
        tokenIn,
        tokenOut,
        amount: amountOut, // exact output
        fee,
        sqrtPriceLimitX96: 0n,
      },
    ],
  });

  return { amountIn, sqrtAfter, ticks, gas };
}

// export async function quoteExactOut({ tokenIn, tokenOut, fee, amountOut }) {
//   if (amountOut === 0n) return 0n;

//   const st = await getPoolStatus(tokenIn, tokenOut, fee);
//   if (!st.exists)
//     throw new Error(`Pool not found for fee ${(fee / 10000).toFixed(2)}%`);
//   if (!st.initialized) throw new Error(`Pool exists but not initialized`);

//   const r = await publicClient.readContract({
//     address: QUOTER_ADDR,
//     abi: QUOTER_ABI,
//     functionName: "quoteExactOutputSingle",
//     args: [[tokenIn, tokenOut, amountOut, fee, 0n]], // ✅ tuple params
//   });

//   // returns [amountIn, sqrtPriceX96After, initializedTicksCrossed, gasEstimate]
//   return Array.isArray(r) ? r[0] : r.amountIn ?? r;
// }

// Prefer exact-output Quoter over binary search; fallback if needed.
export async function deriveMaxInForExactOut({
  tokenIn,
  tokenOut,
  fee,
  amountOut,
}) {
  try {
    const ain = await quoteExactOut({ tokenIn, tokenOut, fee, amountOut });
    return ain;
  } catch (e) {
    // Fallback to binary search if quoteExactOut not available for any reason
    const st = await getPoolStatus(tokenIn, tokenOut, fee);
    if (!st.exists)
      throw new Error(`Pool not found for fee ${(fee / 10000).toFixed(2)}%`);
    if (!st.initialized)
      throw new Error(
        `Pool exists at ${(fee / 10000).toFixed(2)}% but is not initialized`
      );

    const quoteIn = (ain) =>
      quoteExactIn({ tokenIn, tokenOut, fee, amountIn: ain });
    let lo = 0n,
      hi = 10n ** 16n;
    for (let i = 0; i < 64; i++) {
      const out = await quoteIn(hi);
      if (out >= amountOut) break;
      hi *= 2n;
    }
    for (let i = 0; i < 48; i++) {
      const mid = (lo + hi) >> 1n;
      const out = await quoteIn(mid);
      if (out >= amountOut) hi = mid;
      else lo = mid;
    }
    return hi;
  }
}

// -------------------- swaps (native-aware) --------------------
/**
 * Swap exact input. Options:
 *  - payWithNative: if tokenIn is WETHW, wrap amountIn before swap (from sender's ETHW)
 *  - unwrapToNative: if tokenOut is WETHW, receive native ETHW via router.multicall
 */
export async function swapExactIn({
  tokenIn,
  tokenOut,
  fee,
  amountIn,
  minOut,
  recipient,
  payWithNative = false,
  unwrapToNative = true,
  translate,
}) {
  const account = await getAccount();
  let wrappedDelta = 0n; // how much THIS call wrapped
  try {
    // If paying with native and input is WETHW, wrap first and ensure approval
    if (payWithNative && isWethw(tokenIn)) {
      // await wrapWETHW(amountIn);
      const pre = await wethwBalance(account);

      await wrapWETHW(amountIn); // wrap tx

      const post = await wethwBalance(account);
      wrappedDelta = post > pre ? post - pre : 0n;

      const allow = await allowance(account, tokenIn, ADDRS.SWAP_ROUTER).catch(
        () => 0n
      );
      if (allow < amountIn) await approve(ADDRS.SWAP_ROUTER, tokenIn, amountIn);
    }
    // If output is WETHW and unwrap requested, use multicall: swap to router → unwrap → refund
    if (unwrapToNative && isWethw(tokenOut)) {
      const swapCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "exactInputSingle",
        args: [
          {
            tokenIn,
            tokenOut,
            fee,
            recipient: ADDRS.SWAP_ROUTER, // router receives WETHW
            deadline: toBigInt(Math.floor(Date.now() / 1000) + 600),
            amountIn,
            amountOutMinimum: minOut,
            sqrtPriceLimitX96: 0n,
          },
        ],
      });
      const unwrapCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "unwrapWETH9",
        args: [0n, recipient ?? account], // unwrap all router-held WETHW to recipient
      });
      const refundCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "refundETH",
        args: [],
      });
      const hash = await walletClient.writeContract({
        account,
        address: ADDRS.SWAP_ROUTER,
        abi: ABIS.router,
        functionName: "multicall",
        args: [[swapCall, unwrapCall, refundCall]],
      });
      // return publicClient.waitForTransactionReceipt({ hash });
      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (!isTxSuccess(receipt))
        throw new Error(`Swap reverted: ${receipt.transactionHash}`);
      return receipt;
    }

    // Normal ERC20→ERC20 (or WETHW→token without unwrapping)
    const hash = await walletClient.writeContract({
      account,
      address: ADDRS.SWAP_ROUTER,
      abi: ABIS.router,
      functionName: "exactInputSingle",
      args: [
        {
          tokenIn,
          tokenOut,
          fee,
          recipient: recipient ?? account,
          deadline: toBigInt(Math.floor(Date.now() / 1000) + 600),
          amountIn,
          amountOutMinimum: minOut,
          sqrtPriceLimitX96: 0n,
        },
      ],
    });

    // return publicClient.waitForTransactionReceipt({ hash });
    const receipt = await publicClient.waitForTransactionReceipt({ hash });

    if (!isTxSuccess(receipt)) {
      throw new Error(`Swap reverted: ${receipt.transactionHash}`);
    }

    return receipt;
  } catch (err) {
    toast.error(err.shortMessage);
    if (wrappedDelta > 0n) {
      toast.info(translate("swap.swapInUnWarappedMsg"));

      await sleep(1500);
      try {
        const resp = await unwrapWETHW(wrappedDelta);
        // toast.success("Unwrapped back to ETHW.");
        toast.success(translate("createLiq.unWrappedSuccessMsg"));
      } catch {
        // toast.error("Unwrap failed. You still have WETHW.");
        toast.error(translate("createLiq.unWrappedErrorMsg"));
      }
    }
    throw err;
  }
}

/**
 * Swap exact output. Options:
 *  - payWithNative: if tokenIn is WETHW, wrap up to maxIn (approve maxIn) before swap
 *  - unwrapToNative: if tokenOut is WETHW, receive native ETHW via router.multicall
 */
export async function swapExactOut({
  tokenIn,
  tokenOut,
  fee,
  amountOut,
  maxIn,
  recipient,
  payWithNative = false,
  unwrapToNative = true,
  translate,
}) {
  const account = await getAccount();
  let wrappedDelta = 0n; // how much THIS call wrapped
  try {
    // If paying with native and input is WETHW, wrap up to maxIn and ensure allowance
    if (payWithNative && isWethw(tokenIn)) {
      // await wrapWETHW(maxIn);
      const pre = await wethwBalance(account);

      await wrapWETHW(maxIn); // wrap tx

      const post = await wethwBalance(account);

      wrappedDelta = post > pre ? post - pre : 0n;

      const allow = await allowance(account, tokenIn, ADDRS.SWAP_ROUTER).catch(
        () => 0n
      );
      if (allow < maxIn) await approve(ADDRS.SWAP_ROUTER, tokenIn, maxIn);
    }

    if (unwrapToNative && isWethw(tokenOut)) {
      const swapCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "exactOutputSingle",
        args: [
          {
            tokenIn,
            tokenOut,
            fee,
            recipient: ADDRS.SWAP_ROUTER, // router receives WETHW
            deadline: toBigInt(Math.floor(Date.now() / 1000) + 600),
            amountOut,
            amountInMaximum: maxIn,
            sqrtPriceLimitX96: 0n,
          },
        ],
      });
      const unwrapCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "unwrapWETH9",
        args: [0n, recipient ?? account],
      });
      const refundCall = encodeFunctionData({
        abi: ABIS.router,
        functionName: "refundETH",
        args: [],
      });

      const hash = await walletClient.writeContract({
        account,
        address: ADDRS.SWAP_ROUTER,
        abi: ABIS.router,
        functionName: "multicall",
        args: [[swapCall, unwrapCall, refundCall]],
      });
      // return publicClient.waitForTransactionReceipt({ hash });
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (!isTxSuccess(receipt))
        throw new Error(`Swap reverted: ${receipt.transactionHash}`);
      return receipt;
    }

    const hash = await walletClient.writeContract({
      account,
      address: ADDRS.SWAP_ROUTER,
      abi: ABIS.router,
      functionName: "exactOutputSingle",
      args: [
        {
          tokenIn,
          tokenOut,
          fee,
          recipient: recipient ?? account,
          deadline: toBigInt(Math.floor(Date.now() / 1000) + 600),
          amountOut,
          amountInMaximum: maxIn,
          sqrtPriceLimitX96: 0n,
        },
      ],
    });
    // return publicClient.waitForTransactionReceipt({ hash });
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (!isTxSuccess(receipt)) {
      throw new Error(`Swap reverted: ${receipt.transactionHash}`);
    }

    return receipt;
  } catch (err) {
    toast.error(err.shortMessage);
    if (wrappedDelta > 0n) {
      toast.info(translate("swap.swapInUnWarappedMsg"));
      await sleep(1500);
      try {
        await unwrapWETHW(wrappedDelta);
        toast.success(translate("createLiq.unWrappedSuccessMsg"));
      } catch {
        toast.error(translate("createLiq.unWrappedErrorMsg"));
      }
    }

    throw err;
  }
}

// -------------------- fee collection --------------------
export async function collectFees({ tokenId, recipient }) {
  const account = await getAccount();
  const to = recipient ?? account;
  const UINT128_MAX = (1n << 128n) - 1n;

  const sim = await publicClient.simulateContract({
    account,
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "collect",
    args: [
      {
        tokenId: toBigInt(tokenId),
        recipient: to,
        amount0Max: UINT128_MAX,
        amount1Max: UINT128_MAX,
      },
    ],
  });

  const hash = await walletClient.writeContract(sim.request);
  const receipt = await publicClient.waitForTransactionReceipt({ hash });
  const [amount0, amount1] = sim.result;
  return { tx: hash, receipt, amount0, amount1 };
}

export async function collectFeesBatch({ tokenIds, recipient }) {
  const out = [];
  for (const id of tokenIds) {
    try {
      const r = await collectFees({ tokenId: id, recipient });
      out.push({ tokenId: id, ok: true, ...r });
    } catch (e) {
      out.push({
        tokenId: id,
        ok: false,
        error: e?.shortMessage || e?.message || String(e),
      });
    }
  }
  return out;
}

export const loadUncollectedForPosition = async (
  tokenId,
  decimals0 = 18,
  decimals1 = 18
) => {
  if (!tokenId) return null;

  const pos = await publicClient.readContract({
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "positions",
    args: [toBigInt(tokenId)],
  });

  const [
    ,
    ,
    token0,
    token1,
    fee,
    tickLower,
    tickUpper,
    liquidity,
    ,
    ,
    tokensOwed0,
    tokensOwed1,
  ] = pos;

  return {
    token0,
    token1,
    fee,
    tickLower,
    tickUpper,
    liquidity,
    tokensOwed0,
    tokensOwed1,
    formatted0: formatUnits(tokensOwed0, decimals0),
    formatted1: formatUnits(tokensOwed1, decimals1),
  };
};

// -------------------- Remove Liquidity --------------------

export async function removeLiquidity({
  tokenId,
  removeBps = 10000,
  slippageBps = 50,
  unwrapToNative = true,
  burnIfEmpty = true,
  debugLabel = "v3.remove",
}) {
  const account = await getAccount();
  const to = account;

  // 1) Read current position
  const posRaw = await publicClient.readContract({
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "positions",
    args: [toBigInt(tokenId)],
  });
  const pos = {
    token0: posRaw[2],
    token1: posRaw[3],
    fee: Number(posRaw[4]),
    tickLower: Number(posRaw[5]),
    tickUpper: Number(posRaw[6]),
    liquidity: posRaw[7], // bigint
    tokensOwed0: posRaw[10],
    tokensOwed1: posRaw[11],
  };
  if (
    pos.liquidity === 0n &&
    pos.tokensOwed0 === 0n &&
    pos.tokensOwed1 === 0n
  ) {
    throw new Error(`[${debugLabel}] Nothing to remove or collect.`);
  }

  if (removeBps <= 0) throw new Error("removeBps must be > 0");
  if (removeBps > 10000) removeBps = 10000;

  const liqToRemove = (pos.liquidity * toBigInt(removeBps)) / 10000n;
  if (liqToRemove === 0n)
    throw new Error(`[${debugLabel}] Computed liquidity to remove is zero.`);

  const deadline = toBigInt(Math.floor(Date.now() / 1000) + 600);
  const slipDiv = 10000n;
  const slipKeep = slipDiv - toBigInt(slippageBps < 0 ? 0 : slippageBps);

  // 2) Simulate decreaseLiquidity to get expected token amounts, then apply slippage mins
  const decSim = await publicClient.simulateContract({
    account,
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "decreaseLiquidity",
    args: [
      {
        tokenId: toBigInt(tokenId),
        liquidity: liqToRemove,
        amount0Min: 0n, // provisional; we’ll set real mins on the actual tx
        amount1Min: 0n,
        deadline,
      },
    ],
  });

  // decSim.result is (amount0, amount1)
  const decOut0 = Array.isArray(decSim.result)
    ? decSim.result[0]
    : decSim.result.amount0 ?? 0n;
  const decOut1 = Array.isArray(decSim.result)
    ? decSim.result[1]
    : decSim.result.amount1 ?? 0n;
  const amount0Min = (decOut0 * slipKeep) / slipDiv;
  const amount1Min = (decOut1 * slipKeep) / slipDiv;

  // 3) Execute decreaseLiquidity with mins
  const decHash = await walletClient.writeContract({
    ...decSim.request,
    args: [
      {
        tokenId: toBigInt(tokenId),
        liquidity: liqToRemove,
        amount0Min,
        amount1Min,
        deadline,
      },
    ],
  });

  const decRc = await publicClient.waitForTransactionReceipt({ hash: decHash });
  if (decRc.status !== "success") {
    const err = new Error(
      `[${debugLabel}] decreaseLiquidity reverted: ${decHash}`
    );
    throw err;
  }

  // 4) Simulate collect to know exact amounts claimable, then collect
  const UINT128_MAX = (1n << 128n) - 1n;
  const colSim = await publicClient.simulateContract({
    account,
    address: ADDRS.POSITION_MANAGER,
    abi: ABIS.npm,
    functionName: "collect",
    args: [
      {
        tokenId: toBigInt(tokenId),
        recipient: to,
        amount0Max: UINT128_MAX,
        amount1Max: UINT128_MAX,
      },
    ],
  });
  const colOut0 = Array.isArray(colSim.result)
    ? colSim.result[0]
    : colSim.result.amount0 ?? 0n;
  const colOut1 = Array.isArray(colSim.result)
    ? colSim.result[1]
    : colSim.result.amount1 ?? 0n;

  const colHash = await walletClient.writeContract(colSim.request);
  const colRc = await publicClient.waitForTransactionReceipt({ hash: colHash });
  if (colRc.status !== "success") {
    const err = new Error(`[${debugLabel}] collect reverted: ${colHash}`);
    throw err;
  }
  // 5) Optional: unwrap WETHW if recipient is caller (we can only safely unwrap from caller’s wallet)
  let unwrapped;
  if (unwrapToNative && to.toLowerCase() === account.toLowerCase()) {
    const t0lc = pos.token0.toLowerCase();
    const t1lc = pos.token1.toLowerCase();
    const wethRecv =
      t0lc === WETHW && colOut0 > 0n
        ? colOut0
        : t1lc === WETHW && colOut1 > 0n
        ? colOut1
        : 0n;

    if (WETHW && wethRecv > 0n) {
      try {
        const uwSim = await publicClient.simulateContract({
          account,
          address: ADDRS.WETHW,
          abi: WETH9_ABI,
          functionName: "withdraw",
          args: [wethRecv],
        });
        const uwHash = await walletClient.writeContract(uwSim.request);
        await publicClient.waitForTransactionReceipt({ hash: uwHash });
        unwrapped = { wethAmount: wethRecv };
      } catch (e) {
        console.warn(
          `[${debugLabel}] unwrap failed:`,
          e?.shortMessage || e?.message || String(e)
        );
      }
    }
  }

  // 6) Optionally burn the NFT if fully emptied (no liquidity, no fees owed)
  if (burnIfEmpty) {
    const after = await publicClient.readContract({
      address: ADDRS.POSITION_MANAGER,
      abi: ABIS.npm,
      functionName: "positions",
      args: [toBigInt(tokenId)],
    });
    const liqAfter = after[7];
    const owed0After = after[10];
    const owed1After = after[11];
    if (liqAfter === 0n && owed0After === 0n && owed1After === 0n) {
      try {
        const burnSim = await publicClient.simulateContract({
          account,
          address: ADDRS.POSITION_MANAGER,
          abi: ABIS.npm,
          functionName: "burn",
          args: [toBigInt(tokenId)],
        });
        const burnHash = await walletClient.writeContract(burnSim.request);
        await publicClient.waitForTransactionReceipt({ hash: burnHash });
      } catch (e) {
        console.warn(
          `[${debugLabel}] burn skipped/failed:`,
          e?.shortMessage || e?.message || String(e)
        );
      }
    }
  }

  const pool = await getPoolAddress(pos.token0, pos.token1, pos.fee);

  const [sqrtPriceX96, tick] = await publicClient.readContract({
    address: pool,
    abi: ABIS.pool,
    functionName: "slot0",
  });

  async function readErc20(publicClient, tokenAddress, fn, args = []) {
    return publicClient.readContract({
      address: tokenAddress,
      abi: ERC20,
      functionName: fn,
      args,
    });
  }

  const t0 = (pos.token0 || "").toLowerCase();
  const t1 = (pos.token1 || "").toLowerCase();
  const weth = ADDRS.WETHW.toLowerCase();

  let tokenAddress = null;
  if (t0 === weth) tokenAddress = pos.token1;
  else if (t1 === weth) tokenAddress = pos.token0;
  else throw new Error("This position is not WETHW/TOKEN pair");

  const tokenReserve = await publicClient.readContract({
    address: tokenAddress,
    abi: ERC20,
    functionName: "balanceOf",
    args: [pool],
  });

  const ethwReserve = await publicClient.readContract({
    address: ADDRS.WETHW,
    abi: ERC20,
    functionName: "balanceOf",
    args: [pool],
  });
  const tokenBalance = await readErc20(
    publicClient,
    tokenAddress,
    "balanceOf",
    [account]
  );

  return {
    decRc,
    colRc,
    sqrtPriceX96,
    tokenReserve,
    ethwReserve,
    tokenBalance,
    decTx: decHash,
    colTx: colHash,
    decreased: { amount0: decOut0, amount1: decOut1, liquidity: liqToRemove },
    collected: { amount0: colOut0, amount1: colOut1 },
    ...(unwrapped ? { unwrapped } : {}),
  };
}

// ----------------------------------validate User OwnsPosition------------------------
const NPM_ABI = [
  {
    type: "function",
    name: "ownerOf",
    stateMutability: "view",
    inputs: [{ name: "id", type: "uint256" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "function",
    name: "positions",
    stateMutability: "view",
    inputs: [{ name: "id", type: "uint256" }],
    outputs: [
      { type: "uint96" },
      { type: "address" },
      { type: "address" },
      { type: "address" },
      { type: "uint24" },
      { type: "int24" },
      { type: "int24" },
      { type: "uint128" },
      { type: "uint256" },
      { type: "uint256" },
      { type: "uint128" },
      { type: "uint128" },
    ],
  },
];

export async function validateUserOwnsPosition({
  tokenId,
  caller,
  posmAddress,
  publicClient,
}) {
  if (!tokenId && tokenId !== 0) return { ok: false, reason: "emptyTokenId" };
  if (!caller) return { ok: false, reason: "noCaller" };

  const id = toBigInt(tokenId);

  let owner;
  try {
    owner = await publicClient.readContract({
      address: posmAddress,
      abi: NPM_ABI,
      functionName: "ownerOf",
      args: [id],
    });
  } catch {
    return { ok: false, reason: "invalidTokenId" };
  }

  if (owner.toLowerCase() !== caller.toLowerCase()) {
    return { ok: false, reason: "notOwner", owner, caller };
  }

  const pos = await publicClient.readContract({
    address: posmAddress,
    abi: NPM_ABI,
    functionName: "positions",
    args: [id],
  });
  const liquidity = pos[7];
  if (liquidity === 0n) {
    return { ok: false, reason: "noLiquidity", owner, caller };
  }

  return { ok: true, owner, caller, liquidity };
}

// ---------------------------------onLockCollectFees-----------------------------------
export async function onLockCollectFees({ lockerAddress, tokenId }) {
  const account = await getAccount();
  const hash = await walletClient.writeContract({
    address: lockerAddress,
    abi: lockerAbi,
    functionName: "collectFees",
    args: [toBigInt(tokenId)],
    account,
  });
  const receipt = await publicClient.waitForTransactionReceipt({ hash });
  return receipt;
}
